import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

/**
 * flatpickr datepicker를 Svelte 액션으로 연결하는 함수
 *
 * @param {HTMLElement} node - input DOM 요소
 * @param {Object} options - 구성 옵션
 * {'start'|'end'} options.type - 시작 날짜인지 종료 날짜인지 구분
 * {Object} options.ref - 외부에서 접근 가능한 컨트롤 함수 객체
 *        (setMinDate, setMaxDate, setStartDate, setEndDate 등 등록됨)
 */
export function datepickerRange(node, { type = 'start', ref = null } = {}) {
    const config = {
        dateFormat: 'Y-m-d', // 표시 및 데이터 포맷
        onChange: function (selectedDates) {
            const formatted = selectedDates[0] ? formatDate(selectedDates[0]) : '';

            // 시작 날짜 선택 시 → 종료 날짜 최소값 제한 및 콜백 호출
            if (type === 'start') {
                ref?.setMinDate?.(selectedDates[0] || null);
                ref?.onStartChange?.(formatted); // 외부에서 포맷된 값 사용 가능
            }

            // 종료 날짜 선택 시 → 시작 날짜 최대값 제한 및 콜백 호출
            if (type === 'end') {
                ref?.setMaxDate?.(selectedDates[0] || null);
                ref?.onEndChange?.(formatted); // 외부에서 포맷된 값 사용 가능
            }
        }
    };

    const instance = flatpickr(node, config);

    if (ref) {
        ref.instance = instance; // flatpickr 인스턴스를 외부에 노출
    }

    // 외부에서 flatpickr 제어할 수 있도록 메서드 등록
    if (type === 'start') {
        ref.setMinDate = (date) => instance.set('minDate', date);
        ref.setStartDate = (date) => instance.setDate(date, true); // ✅ UI도 반영
    }
    if (type === 'end') {
        ref.setMaxDate = (date) => instance.set('maxDate', date);
        ref.setEndDate = (date) => instance.setDate(date, true);   // ✅ UI도 반영
    }

    return {
        destroy() {
            instance.destroy();
        }
    };
}

/**
 * 날짜 프리셋 키워드에 따라 날짜 범위를 계산해 반환
 *
 * @param {string} preset - 'today', 'thisWeek', 'lastMonth' 등 프리셋 키
 * @returns {{startDate: string, endDate: string} | null}
 */
export function getDateRangeFromPreset(preset) {
    const today = new Date();
    let startDate, endDate;

    const clone = (date) => new Date(date.getTime()); // 안전 복제

    switch (preset) {
        case 'today':
            startDate = endDate = clone(today);
            break;

        case 'yesterday':
            startDate = endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
            break;

        case 'thisWeek': {
            // 이번 주 월요일 ~ 일요일
            const day = today.getDay(); // 0 = 일요일, 1 = 월요일, ...
            const offset = day === 0 ? 6 : day - 1;

            startDate = new Date(today);
            startDate.setDate(today.getDate() - offset); // 월요일

            endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6); // 일요일
            break;
        }

        case 'lastWeek': {
            // 지난 주 월요일 ~ 일요일
            const day = today.getDay() || 7;
            endDate = new Date(today);
            endDate.setDate(today.getDate() - day); // 지난 일요일

            startDate = new Date(endDate);
            startDate.setDate(endDate.getDate() - 6); // 지난 월요일
            break;
        }

        case 'thisMonth':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1); // 1일
            endDate = today; // 오늘
            break;

        case 'lastMonth':
            startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1); // 지난달 1일
            endDate = new Date(today.getFullYear(), today.getMonth(), 0);      // 지난달 마지막 날
            break;

        case 'all':
            return { startDate: '', endDate: '' }; // 제한 없음
        
        case 'custom':
            return { startDate: '', endDate: '' }; // 제한 없음
        
        default:
            return null; // 예외 처리 (예: 'custom')
    }

    return {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate)
    };
}

/**
 * flatpickr 인스턴스의 날짜를 완전히 재설정 (clear 후 setDate)
 *
 * @param {Object} instance - flatpickr instance
 * @param {Date} date - 설정할 날짜
 */
export function resetFlatpickrDate(instance, date) {
    if (!instance || typeof instance.clear !== 'function') return;
    instance.clear();
    instance.setDate(date, true, 'Y-m-d');
}

/**
 * Date 객체를 'YYYY-MM-DD' 문자열로 포맷
 *
 * @param {Date} date
 * @returns {string}
 */
export function formatDate(date) {
    if (!(date instanceof Date)) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}
